import { confirmationOf, type CashierConfirmation, type CashierListState, type CashierProblem, type PosCashierChoice, type PosCashierGrant } from './cashier'

/**
 * Кассир окна «РМК» «Собственной» (RP-OWN11, часть Б): выбранный из списка, его подтверждение кодом и ход проверки. Чистые
 * переходы без React и сети; слайс (`cashierSlice.ts`) раскладывает их по источникам. Код здесь не бывает вовсе: он приходит
 * аргументом `verify` и уходит в тело одного запроса. Токен подтверждения (`grant`) тоже не хранится — только кто и до какого времени.
 */
export interface CashierState {
  list: CashierListState
  selectedId: string | null
  grant: CashierConfirmation | null
  /** Подтверждение было и истекло: нужен код ещё раз. */
  expired: boolean
  verifying: boolean
  problem: CashierProblem | null
}

export type CashierAction =
  | { type: 'reset' }
  | { type: 'list-ready'; items: readonly PosCashierChoice[] }
  | { type: 'list-error'; problem: CashierProblem }
  | { type: 'selected'; id: string | null }
  | { type: 'verify-started' }
  | { type: 'verify-ok'; grant: PosCashierGrant }
  | { type: 'verify-failed'; problem: CashierProblem }
  | { type: 'expired' }
  | { type: 'forgotten' }

export const initialCashier: CashierState = { list: { status: 'loading' }, selectedId: null, grant: null, expired: false, verifying: false, problem: null }

export function cashierReducer(state: CashierState, action: CashierAction): CashierState {
  switch (action.type) {
    case 'reset':
      return initialCashier
    case 'list-ready': {
      // Кассира убрали из списка (архив): его выбор и подтверждение больше не действуют.
      const kept = state.selectedId !== null && action.items.some((item) => item.sourceCashierId === state.selectedId)
      return { ...state, list: { status: 'ready', items: action.items }, selectedId: kept ? state.selectedId : null, grant: kept ? state.grant : null }
    }
    case 'list-error':
      return { ...state, list: { status: 'error', problem: action.problem } }
    case 'selected':
      // Подтверждение принадлежит тому, кого подтвердили: другому кассиру оно не переходит.
      return state.selectedId === action.id ? state : { ...state, selectedId: action.id, grant: null, expired: false, problem: null }
    case 'verify-started':
      return { ...state, verifying: true, problem: null }
    case 'verify-ok':
      return { ...state, verifying: false, grant: confirmationOf(action.grant), expired: false, problem: null }
    case 'verify-failed':
      return { ...state, verifying: false, problem: action.problem }
    case 'expired':
      return state.grant === null ? state : { ...state, grant: null, expired: true }
    case 'forgotten':
      return { ...state, selectedId: null, grant: null, expired: false, problem: null }
  }
}
